import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyDocuments } from 'src/api/company';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/documents/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			filter: (search.filter as string) || undefined,
			page: (search.page as number) || undefined,
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({
		page: searchParams.page || 0,
		all: searchParams.filter === 'all' ? 'yes' : '',
	});

	const { data: documents, isLoading: docsLoading } = useQuery({
		queryKey: ['company-documents', groupId, searchParams],
		queryFn: () => getCompanyDocuments(groupId, query),
		enabled: !userLoading,
	});

	const { data: documentTypes, isLoading: typesLoading } = useQuery({
		queryKey: ['company-document-types'],
		queryFn: () => getDocumentTypes('groups'),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(1, 'Documents');

	if (userLoading || docsLoading || typesLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	// Permission check
	if (!verifyPermission(PERMISSIONS.companyOverviewView)) {
		navigate({ to: '/company/$groupId/information', params: { groupId }, replace: true });
		return null;
	}

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-documents', groupId] });
	};

	return (
		<div>
			<Paper variant="outlined" className="mt-2">
				<DocumentTab
					data={documents}
					documentTypes={documentTypes?.data || []}
					revalidateDetail={handleReload}
					apiUrl={`/api/company-portal/groups/${groupId}/files`}
					showFilterAndActionBox={true}
					onReload={handleReload}
					onSelectDocument={(id: string) =>
						navigate({
							to: '/company/$groupId/documents/$documentId',
							params: { groupId, documentId: id },
						})
					}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.companyDocumentsView]),
						fullView: verifyPermission([PERMISSIONS.companyDocumentsView]),
						upload: verifyPermission([PERMISSIONS.companyDocumentsCreate]),
						archive: verifyPermission([PERMISSIONS.companyDocumentsEdit]),
						edit: verifyPermission([PERMISSIONS.companyDocumentsEdit]),
					}}
				/>
			</Paper>
		</div>
	);
}
